import { useCallback, useEffect, useRef, useState } from "react";
import { getRushSessionPopupMinutes } from "../services/helpers/session-popup";

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "click", "scroll"];
const MOUSE_MOVE_THROTTLE_MS = 1000;

export default function useSessionInactivityPopup(login) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const lastMouseMoveRef = useRef(0);
  const minutes = login ? getRushSessionPopupMinutes(login) : null;

  const clearTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const scheduleTimer = useCallback(() => {
    clearTimer();

    if (!minutes) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      setOpen(true);
    }, minutes * 60 * 1000);
  }, [minutes]);

  const handleActivity = useCallback(() => {
    if (open) {
      return;
    }

    scheduleTimer();
  }, [open, scheduleTimer]);

  const handleMouseMove = useCallback(() => {
    const now = Date.now();

    if (now - lastMouseMoveRef.current < MOUSE_MOVE_THROTTLE_MS) {
      return;
    }

    lastMouseMoveRef.current = now;
    handleActivity();
  }, [handleActivity]);

  const handleClose = useCallback(() => {
    setOpen(false);
    scheduleTimer();
  }, [scheduleTimer]);

  useEffect(() => {
    if (!minutes) {
      clearTimer();
      setOpen(false);
      return undefined;
    }

    scheduleTimer();

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    window.addEventListener("mousemove", handleMouseMove, { passive: true });

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [minutes, scheduleTimer, handleActivity, handleMouseMove]);

  return { open, handleClose };
}
